import { useState, useEffect, useCallback } from 'react'
import { fetchWithAuth } from '@/lib/api'

export type ZoomAuthStatus = {
  connected: boolean
  configured: boolean
  missing?: string[]
  error?: string
  hint?: string
  organizationId?: string
}

function orgQs(organizationId?: string | null): string {
  if (!organizationId) return ''
  return `?organizationId=${encodeURIComponent(organizationId)}`
}

export function useZoomAuth(organizationId?: string | null) {
  const [authStatus, setAuthStatus] = useState<ZoomAuthStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const res = await fetchWithAuth(`/api/zoom/auth/status${orgQs(organizationId)}`)
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(typeof body?.error === 'string' ? body.error : 'Failed to check Zoom status')
      }
      const data = (await res.json()) as ZoomAuthStatus
      setAuthStatus(data)
      return data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check Zoom status')
      setAuthStatus({ connected: false, configured: false })
      return null
    } finally {
      setIsLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    void refetch()
  }, [refetch])

  const disconnect = useCallback(async () => {
    const res = await fetchWithAuth(`/api/zoom/auth/disconnect${orgQs(organizationId)}`, {
      method: 'DELETE',
    })
    if (res.ok) {
      setAuthStatus({ connected: false, configured: false })
    }
    return res.ok
  }, [organizationId])

  return { authStatus, isLoading, error, refetch, disconnect }
}
